import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { DOCUMENT } from '../constants/documentTheme';
import { partnerOxbloodBtnSx } from './PartnerListChrome';

const RelistPackageDialog = ({
  pkg,
  open,
  loading = false,
  error = '',
  onClose,
  onSubmit,
  onOpenEditor,
}) => {
  const { t } = useTranslation();
  const [availableFrom, setAvailableFrom] = useState('');
  const [availableUntil, setAvailableUntil] = useState('');
  const [localError, setLocalError] = useState('');

  useEffect(() => {
    if (!open) return;
    setAvailableFrom(format(new Date(), 'yyyy-MM-dd'));
    setAvailableUntil('');
    setLocalError('');
  }, [open, pkg?.id]);

  const expiredDate = pkg?.available_until
    ? format(new Date(pkg.available_until), 'd MMM yyyy')
    : '';

  const handleSubmit = (event) => {
    event.preventDefault();
    if (availableFrom && availableUntil && availableFrom > availableUntil) {
      setLocalError(t('partnerLists.relistRange'));
      return;
    }
    const today = format(new Date(), 'yyyy-MM-dd');
    if (availableUntil && availableUntil < today) {
      setLocalError(t('partnerLists.relistUntilPast'));
      return;
    }
    setLocalError('');
    onSubmit({
      available_from: availableFrom || null,
      available_until: availableUntil || null,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      data-testid="relist-package-dialog"
      TransitionProps={{ timeout: 0 }}
      disableScrollLock
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>{t('partnerLists.relistTitle')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2, color: DOCUMENT.ink2, lineHeight: 1.6 }}>
            {expiredDate
              ? t('partnerLists.relistBody', { title: pkg?.title, date: expiredDate })
              : t('partnerLists.relistBodyNoDate', { title: pkg?.title })}
          </Typography>
          <TextField
            label={t('partnerLists.relistFrom')}
            type="date"
            value={availableFrom}
            onChange={(event) => setAvailableFrom(event.target.value)}
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
            inputProps={{ 'data-testid': 'relist-available-from' }}
          />
          <TextField
            label={t('partnerLists.relistUntil')}
            type="date"
            value={availableUntil}
            onChange={(event) => setAvailableUntil(event.target.value)}
            fullWidth
            margin="normal"
            helperText={t('partnerLists.relistUntilHint')}
            InputLabelProps={{ shrink: true }}
            inputProps={{ 'data-testid': 'relist-available-until' }}
          />
          {(localError || error) ? (
            <Alert severity="error" sx={{ mt: 1.5 }}>{localError || error}</Alert>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'wrap' }}>
          <Button type="button" onClick={onClose} disabled={loading}>
            {t('partnerLists.cancel')}
          </Button>
          {onOpenEditor ? (
            <Button type="button" onClick={onOpenEditor} disabled={loading}>
              {t('partnerLists.relistOpenEditor')}
            </Button>
          ) : null}
          <Button type="submit" sx={partnerOxbloodBtnSx} disabled={loading}>
            {loading ? <CircularProgress size={20} color="inherit" /> : t('partnerLists.relistSubmit')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default RelistPackageDialog;
